import { ApiBlueprint, createApiRef } from '@backstage/frontend-plugin-api';
import {
  analyticsApiRef,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  oauthRequestApiRef,
} from '@backstage/core-plugin-api';
import type {
  OAuthApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
  scmAuthApiRef,
} from '@backstage/integration-react';
import { GoogleAnalytics4 } from '@backstage-community/plugin-analytics-module-ga4';
import { GithubAuth } from '@backstage/core-app-api';
import { ragAiApiRef, RoadieRagAiClient } from '@roadiehq/rag-ai';
import {
  entityPreviewApiRef,
  EntityPreviewClient,
} from '@schibsted/backstage-plugin-entity-preview-react';
import {
  apiDocsConfigRef,
  defaultDefinitionWidgets,
  OpenApiDefinitionWidget,
} from '@backstage/plugin-api-docs';
import type { ApiEntity } from '@backstage/catalog-model';

export const ghecAuthApiRef = createApiRef<
  OAuthApi & ProfileInfoApi & SessionApi
>({
  id: 'internal.auth.production',
});

export const analyticsApi = ApiBlueprint.make({
  name: 'analytics',
  params: defineParams =>
    defineParams({
      api: analyticsApiRef,
      deps: { configApi: configApiRef, identityApi: identityApiRef },
      factory: ({ configApi, identityApi }) =>
        GoogleAnalytics4.fromConfig(configApi, { identityApi }),
    }),
});

export const scmIntegrationsApi = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams({
      api: scmIntegrationsApiRef,
      deps: { configApi: configApiRef },
      factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
});

export const ghecAuthApi = ApiBlueprint.make({
  name: 'ghec-auth',
  params: defineParams =>
    defineParams({
      api: ghecAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        GithubAuth.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: 'production',
          provider: {
            id: 'github',
            title: 'GitHub Enterprise Cloud',
            icon: () => null,
          },
          defaultScopes: ['read:user'],
        }),
    }),
});

export const scmAuthApi = ApiBlueprint.make({
  name: 'scm-auth',
  params: defineParams =>
    defineParams({
      api: scmAuthApiRef,
      deps: { ghecAuthApi: ghecAuthApiRef },
      factory: ({ ghecAuthApi: authApi }) =>
        ScmAuth.merge(
          ScmAuth.forGithub(authApi, { host: 'schibsted.ghe.com' }),
        ),
    }),
});

export const ragAiApi = ApiBlueprint.make({
  name: 'rag-ai',
  params: defineParams =>
    defineParams({
      api: ragAiApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        configApi: configApiRef,
        identityApi: identityApiRef,
      },
      factory: ({ discoveryApi, fetchApi, configApi, identityApi }) =>
        new RoadieRagAiClient({
          discoveryApi,
          fetchApi,
          configApi,
          identityApi,
        }),
    }),
});

export const entityPreviewApi = ApiBlueprint.make({
  name: 'entity-preview',
  params: defineParams =>
    defineParams({
      api: entityPreviewApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new EntityPreviewClient(discoveryApi, fetchApi),
    }),
});

export const apiDocsConfigApi = ApiBlueprint.make({
  name: 'api-docs-config',
  params: defineParams =>
    defineParams({
      api: apiDocsConfigRef,
      deps: { identityApi: identityApiRef, discoveryApi: discoveryApiRef },
      factory: ({ identityApi, discoveryApi }) => {
        const requestInterceptor = async (req: Record<string, any>) => {
          const { token } = await identityApi.getCredentials();

          const requestUrl = new URL(req.url);
          const proxyBaseUrl = await discoveryApi.getBaseUrl('proxy');
          const swaggerProxyUrl = `${proxyBaseUrl}/swagger`;
          const finalRequestUrl = `${swaggerProxyUrl}${requestUrl.pathname}${requestUrl.search}`;

          return {
            ...req,
            url: finalRequestUrl,
            headers: {
              ...req.headers,
              Authorization: `Bearer ${token}`,
              'X-Target-Authorization': req.headers.Authorization,
              'X-Target-Origin': requestUrl.origin,
            },
          };
        };

        const definitionWidgets = defaultDefinitionWidgets().map(obj => {
          if (obj.type === 'openapi') {
            return {
              ...obj,
              component: (definition: string) => (
                <OpenApiDefinitionWidget
                  definition={definition}
                  requestInterceptor={requestInterceptor}
                  {...({ persistAuthorization: true } as Record<
                    string,
                    unknown
                  >)}
                />
              ),
            };
          }
          return obj;
        });

        return {
          getApiDefinitionWidget: (apiEntity: ApiEntity) =>
            definitionWidgets.find(d => d.type === apiEntity.spec.type),
        };
      },
    }),
});
